import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import type { DashboardModeSwitchModel } from '@/hooks/useDashboardModeSwitch';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

interface DashboardModePillProps {
  model: DashboardModeSwitchModel;
}

export function DashboardModePill({ model }: DashboardModePillProps) {
  const navigate = useNavigate();
  const {
    activeSide,
    leftPath,
    rightPath,
    leftTooltip,
    rightTooltip,
    LeftIcon,
    RightIcon,
  } = model;

  const segmentClass = (side: 'left' | 'right') =>
    cn(
      'flex flex-1 items-center justify-center min-w-9 px-2.5 transition-colors outline-none',
      'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900',
      activeSide === side
        ? 'bg-primary-100 dark:bg-primary-900/35 text-primary-700 dark:text-primary-300'
        : 'bg-sidebar text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
    );

  return (
    <TooltipProvider delayDuration={300}>
      <div
        id={TOUR_STEP_IDS.DASHBOARD_MODE_SWITCH}
        className={cn(
          'inline-flex h-9 shrink-0 overflow-hidden rounded-full border border-sidebar-border',
          'bg-sidebar select-none'
        )}
        role="group"
        aria-label="Dashboard mode"
      >
        <TooltipRoot>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(segmentClass('left'), 'rounded-l-full')}
              aria-pressed={activeSide === 'left'}
              aria-label={leftTooltip}
              onClick={() => navigate(leftPath)}
            >
              <LeftIcon className="h-4 w-4 shrink-0" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{leftTooltip}</TooltipContent>
        </TooltipRoot>

        <div
          className="w-px shrink-0 self-stretch bg-sidebar-border"
          aria-hidden
        />

        <TooltipRoot>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(segmentClass('right'), 'rounded-r-full')}
              aria-pressed={activeSide === 'right'}
              aria-label={rightTooltip}
              onClick={() => navigate(rightPath)}
            >
              <RightIcon className="h-4 w-4 shrink-0" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{rightTooltip}</TooltipContent>
        </TooltipRoot>
      </div>
    </TooltipProvider>
  );
}
